import { Paths } from "@constants/paths";
import useUserMode from "@hooks/useUserMode";
import { RootState } from "@stores";
import logger from "@utils/logger";
import { PropsWithClassName, cn } from "kz-ui-sdk";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { matchPath, useLocation } from "react-router-dom";

interface AppVersionProps extends PropsWithClassName {
  format?: string;
  versionFileUrl?: string;
}

const API_URLS: Record<string, string[]> = {
  ["STG - PH1"]: ["https://api.tph1.trykz.dev"],
  ["STG - TH1"]: ["https://api.tth1.trykz.dev"],
  ["STG - PG1"]: ["https://api.tpg1.trykz.dev"],
  ["STG - TG1"]: ["https://api.ttg1.trykz.dev"],
  ["PRD - PH1"]: ["https://api.phkz1api.com"],
};

const AppVersion = ({ format = "vYYMMDDHHmm", className }: AppVersionProps) => {
  const [versionNumber, setVersionNumber] = useState<string | undefined>();
  const [serverName, setServerName] = useState<string>();
  const isWsConnected = useSelector((state: RootState) => state?.preferences?.isWsConnected);
  const { isMember } = useUserMode();
  const location = useLocation();

  const getVersionNumber = useCallback(async () => {
    try {
      const version = import.meta.env.VITE_APP_VERSION;
      const versionNum = moment(version).format(format);
      setVersionNumber(versionNum);
    } catch (error) {
      logger._console.log("Running in development mode");
    }
  }, [format]);

  useEffect(() => {
    getVersionNumber().then(() => {});
  }, [getVersionNumber]);

  useEffect(() => {
    // Only display server name in DEV environment
    if (import.meta.env.DEV) {
      const baseAPI = import.meta.env.VITE_API_BASE_URI;
      const serverName = Object.keys(API_URLS).find((key) => API_URLS[key].includes(baseAPI));
      setServerName(serverName);
    }
  }, []);

  const isShowWSIndicator = useMemo(() => {
    return isMember && !matchPath(Paths.PRIVATE.DEPOSIT_CALLBACK, location.pathname);
  }, [isMember, location.pathname]);

  return (
    <div className="pointer-events-none fixed bottom-0 left-1/2 z-[9999] w-full max-w-md -translate-x-1/2">
      <span
        className={cn(
          "absolute bottom-1 right-2.5 origin-bottom-right rotate-90 text-[7px] text-content-soft [text-shadow:0.25px_0.25px_0px_#000]",
          className,
        )}
      >
        {isShowWSIndicator && (
          <span
            className={cn(
              "absolute -left-1.5 bottom-1 flex size-1 rounded-full",
              isWsConnected ? "bg-success-base" : "bg-content-soft",
            )}
          />
        )}
        {versionNumber ?? moment().format(format)}
      </span>
      {serverName && (
        <span
          className={cn(
            "absolute bottom-1 left-3 origin-bottom-left -rotate-90 text-[7px] text-content-soft [text-shadow:0.25px_0.25px_0px_#000]",
            {
              "text-yellow-400": serverName.startsWith("STG"),
              "text-red-400": serverName.startsWith("PRD"),
            },
            className,
          )}
        >
          {serverName}
        </span>
      )}
    </div>
  );
};

export default AppVersion;
